import { defineStore } from 'pinia';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import { getCurrentDate, downloadFileFromMemory } from '@/utils';

import {
  isValidContextPeriodMode,
  isValidReportingPeriodMode,
  REPORTING_PERIOD_MODES,
  CONTEXT_PERIOD_MODES,
  updateDateRange,
} from '@/components/core/comparison-date-picker/constants';
import { DashboardAPI, FacebookAPI } from '@/apis';
import * as FacebookAllAPI from '@/apis/facebook';

export const REPORT_RANGE_DEFAULT = [getCurrentDate(7), getCurrentDate(1)];
export const CONTEXT_RANGE_DEFAULT = [getCurrentDate(14), getCurrentDate(8)];

export function formatSummaryMetrics(responseMetrics) {
  if (!responseMetrics) return {};
  const result = cloneDeep(responseMetrics);

  Object.keys(result).forEach((key) => {
    if (result[key].FACEBOOK && result[key].FACEBOOK.value == null) {
      result[key].FACEBOOK.value = '-';
    }
  });
  return result;
}

function toCampaignMap(campaignList) {
  return (campaignList ?? []).reduce((acc, campaign) => {
    acc[campaign.sourceCampaignId] = campaign;
    return acc;
  }, {});
}

export const useFacebookStore = defineStore('facebook', {
  resetOnBrandChange: true,
  state: () => ({
    pending: {
      pageMetrics: {
        summary_metrics: false,
        timeseries_metrics: false,
        past_twelve_weeks: false,
      },
      marketingV2Campaigns: false,
      marketingV2Ads: false,
      fbAdsAccounts: false,
      sendImagesToMetaAds: false,
      createMarketingV2Ads: false,
    },
    campaignList: [],
    fbAdsAccounts: [],
    hasApiAccess: false,
    adAccounts: {},
    adAccountsLoading: false,
    pageFansCSV: null,
    pageMetricsCSV: null,
    topPostsCSV: null,
    allPostsCSV: null,
    exportPending: false,
    pageMetrics: {
      summary_metrics: {},
      timeseries_metrics: {},
      past_twelve_weeks: {},
    },
    twelveWeekAverageEngagement: null,
    reportDateRange: cloneDeep(REPORT_RANGE_DEFAULT),
    contextDateRange: cloneDeep(CONTEXT_RANGE_DEFAULT),
    marketingV2Campaigns: {},
    marketingV2Ads: [],
    marketingV2AdsNextOffset: null,
    marketingV2AdsCount: 0,
    error: {
      adAccounts: false,
      campaigns: false,
    },
  }),
  getters: {
    adAccountsList(state) {
      const clonedAdAccounts = cloneDeep(state.adAccounts);
      const uniqAdAccounts = Object.values(
        Object.entries(clonedAdAccounts).reduce((acc, [brandId, adAccounts]) => {
          const brandIdAsInt = parseInt(brandId, 10);
          for (let i = 0; i < adAccounts?.length; i += 1) {
            const adAccount = adAccounts[i];
            if (!acc[adAccount.accountId]) {
              acc[adAccount.accountId] = adAccount;
              acc[adAccount.accountId].brandIds = [brandIdAsInt];
            } else if (!acc[adAccount.accountId].brandIds.includes(brandIdAsInt)) {
              acc[adAccount.accountId].brandIds.push(brandIdAsInt);
            }
          }
          return acc;
        }, {}),
      );
      const sortedUniqAdAccounts = uniqAdAccounts.sort((adAccountA, adAccountB) =>
        adAccountA.accountName.localeCompare(adAccountB.accountName),
      );
      return sortedUniqAdAccounts;
    },
    metaAdsAccountToCurrency() {
      const mapping = {};
      this.adAccountsList.forEach((account) => {
        mapping[account.accountId] = account.currency;
      });
      return mapping;
    },
  },
  actions: {
    updateReportingPeriodMode(mode) {
      let valueToBeUsed = null;
      if (isValidReportingPeriodMode(mode)) {
        valueToBeUsed = mode;
      } else {
        valueToBeUsed = REPORTING_PERIOD_MODES.LAST_7_DAYS.value;
      }

      this.reportingPeriodMode = valueToBeUsed;
    },
    updateContextPeriodMode(mode) {
      this.reportingPeriodMode = isValidContextPeriodMode(mode)
        ? mode
        : CONTEXT_PERIOD_MODES.PREVIOUS_WEEK.value;
    },

    initDateRanges({
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      reportingPeriodMode,
      contextPeriodMode,
    }) {
      this.updateReportDate({ reportDateRange: [startDate, endDate] });
      this.updateContextDate({ contextDateRange: [contextStartDate, contextEndDate] });
      this.updateReportingPeriodMode(reportingPeriodMode);
      this.updateContextPeriodMode(contextPeriodMode);
    },
    updateReportDate({ reportDateRange }) {
      updateDateRange(this.$state, 'reportDateRange', reportDateRange, REPORT_RANGE_DEFAULT);
    },
    updateContextDate({ contextDateRange }) {
      updateDateRange(this.$state, 'contextDateRange', contextDateRange, CONTEXT_RANGE_DEFAULT);
    },
    updateDateRanges({ newReportDateRange, newContextDateRange }) {
      const hasNewReportDateRange = newReportDateRange?.length > 0;
      const hasNewContextDateRange = newContextDateRange?.length > 0;

      const hasUpdatedReportDateRange = !isEqual(newReportDateRange, this.reportDateRange);
      const hasUpdatedContextDateRange = !isEqual(newContextDateRange, this.contextDateRange);

      if (hasNewReportDateRange && hasUpdatedReportDateRange) {
        this.updateReportDate({ reportDateRange: newReportDateRange });
      }

      if (hasNewContextDateRange && hasUpdatedContextDateRange) {
        this.updateContextDate({ newContextDateRange });
      }
    },
    async getPageMetrics({ brandId, startDate, endDate, scale, metrics }) {
      this.pending.pageMetrics = {
        summary_metrics: true,
        timeseries_metrics: true,
        past_twelve_weeks: true,
      };
      this.pageMetrics = {
        summary_metrics: {},
        timeseries_metrics: {},
        past_twelve_weeks: {},
      };
      await this.getTwelveWeeksData({
        brandId,
        startDate: startDate.slice(0, 10),
        endDate: endDate.slice(0, 10),
        scale,
      });

      await this.getTopLineMetrics({
        brandId,
        startDate,
        endDate,
        requestedMetrics: metrics,
        contextStartDate: startDate, // TODO: Facebook overview should support context dates
        contextEndDate: endDate, // TODO: Facebook overview should support context dates
      });
      await this.getGraphStats({
        brandIds: brandId,
        startDate,
        endDate,
        contextStartDate: startDate, // TODO: Facebook overview should support context dates
        contextEndDate: endDate, // TODO: Facebook overview should support context dates
        timeScale: scale,
      });
    },
    async getTwelveWeeksData({ brandId, startDate, endDate, scale }) {
      this.pending.pageMetrics.past_twelve_weeks = true;
      try {
        const response = await FacebookAPI.getPageMetrics({ brandId, startDate, endDate, scale });

        const data = response?.data;
        this.pageMetrics.past_twelve_weeks = { ...data.past_twelve_weeks };
        this.twelveWeekAverageEngagement = Math.round(
          this.pageMetrics?.past_twelve_weeks?.avg_engagements,
        );
      } catch (error) {
        if (error?.response?.status === 404) {
          this.pageMetrics.past_twelve_weeks = {};
        } else {
          throw error;
        }
      } finally {
        this.pending.pageMetrics.past_twelve_weeks = false;
      }
      return this.pageMetrics.past_twelve_weeks;
    },
    async getTopLineMetrics({
      brandId,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      requestedMetrics,
    }) {
      this.pending.pageMetrics.summary_metrics = true;

      try {
        const response = await DashboardAPI.getTopLineStatsForChannel({
          brandIds: brandId,
          requirePosts: true,
          startDate,
          endDate,
          contextStartDate,
          contextEndDate,
          channels: 'FACEBOOK',
          metrics: requestedMetrics.join(','),
        });
        this.pageMetrics.summary_metrics = formatSummaryMetrics(
          response.data.data[brandId]?.metrics,
        );
        this.pending.pageMetrics.summary_metrics = false;
      } catch (error) {
        if (error?.response?.status === 404) {
          this.pageMetrics.summary_metrics = {};
          this.pending.pageMetrics.summary_metrics = false;
        } else if (error?.code !== 'ERR_CANCELED') {
          this.pending.pageMetrics.summary_metrics = false;
          throw error;
        }
      }
      return this.pageMetrics.summary_metrics;
    },
    async getGraphStats({
      brandIds,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      timeScale,
    }) {
      this.pending.pageMetrics.timeseries_metrics = true;
      try {
        const response = await DashboardAPI.getGraphStats({
          brandIds,
          startDate,
          endDate,
          contextStartDate,
          contextEndDate,
          timeScale,
          channels: 'FACEBOOK',
          metrics:
            'NET_NEW_FOLLOWERS,TOTAL_FOLLOWERS,NET_NEW_PAGE_LIKES,TOTAL_PAGE_LIKES,TOTAL_ENGAGEMENTS,AVG_ENGAGEMENT_RATE,AVG_EFFECTIVENESS,TOTAL_POST_REACH,IMPRESSIONS,LINK_CLICKS,AVG_ENGAGEMENT_RATE_V2,AVG_EFFECTIVENESS_V2,TOTAL_ENGAGEMENTS_POSTS_V2,PAID_AND_ORGANIC_POST_REACH_V2,PAID_AND_ORGANIC_VIEWS_BY_POST',
        });
        this.pageMetrics.timeseries_metrics = response.data?.data?.[brandIds]?.FACEBOOK;
        this.pending.pageMetrics.timeseries_metrics = false;
      } catch (error) {
        if (error?.response?.status === 404) {
          this.pageMetrics.timeseries_metrics = {};
          this.pending.pageMetrics.timeseries_metrics = false;
        } else if (error?.code !== 'ERR_CANCELED') {
          this.pending.pageMetrics.timeseries_metrics = false;
          throw error;
        }
      }
      return this.pageMetrics.timeseries_metrics;
    },
    async getCampaignList({ brandId }) {
      const response = FacebookAPI.getCampaignList({ brandId });
      this.campaignList = response?.data;
    },
    async fetchFbAccountsFromApi({ brandId, fetchApi, connectFacebookAdsToken }) {
      this.pending.fbAdsAccounts = true;
      try {
        const response = await FacebookAPI.getAccountsFromApi({
          brandId,
          fetchApi,
          connectFacebookAdsToken,
        });
        const data = response?.data || [];
        this.fbAdsAccounts = data.map((item) => ({ ...item, brandId }));
      } finally {
        this.pending.fbAdsAccounts = false;
      }
    },
    async fetchFbAccountsFromApiV2({ brandId, connectFacebookAdsToken }) {
      this.pending.fbAdsAccounts = true;
      try {
        const response = await FacebookAPI.getAccountsFromApiV2({
          brandId,
          connectFacebookAdsToken,
        });
        const data = response?.data || [];
        this.fbAdsAccounts = data.accounts.map((item) => ({ ...item, brandId }));
        this.hasApiAccess = data.has_api_access;
      } finally {
        this.pending.fbAdsAccounts = false;
      }
    },
    async getAdAccounts({ brandIds }) {
      const brandIdsToRequest = (brandIds ?? []).filter((brandId) => !this.adAccounts[brandId]);
      if (brandIdsToRequest.length === 0) return this.adAccounts;

      this.adAccountsLoading = true;
      try {
        const { data } = await FacebookAPI.getAccountsMultiBrandsFromApi(
          { brandIds: brandIdsToRequest },
          { camelizeKeys: true },
        );
        this.adAccounts = merge(this.adAccounts, data) ?? {};
      } catch (error) {
        if (error?.response?.status >= 400) {
          this.error.adAccounts = true;
        }
      } finally {
        this.adAccountsLoading = false;
      }
      return this.adAccounts;
    },
    clearAdAccounts() {
      this.adAccounts = {};
      this.error.adAccounts = false;
    },
    async getMarketingV2Campaigns({ brandIds, adAccountIds, sourceCampaignIds }, axiosConfig) {
      const outstandingSourceCampaignIds = sourceCampaignIds?.filter(
        (sourceCampaignId) => !this.marketingV2Campaigns[sourceCampaignId],
      );

      if (
        (brandIds?.length > 0 && adAccountIds?.length > 0) ||
        outstandingSourceCampaignIds?.length > 0
      ) {
        this.pending.marketingV2Campaigns = true;
        try {
          const response = await FacebookAllAPI.getMarketingV2Campaigns(
            {
              brandIds,
              adAccountIds,
              sourceCampaignIds: outstandingSourceCampaignIds,
            },
            axiosConfig,
          );
          const payload = response?.data;
          if (payload) {
            const newCampaignMap = toCampaignMap(payload);
            this.marketingV2Campaigns = {
              ...this.marketingV2Campaigns,
              ...newCampaignMap,
            };
          }
        } catch (error) {
          if (error?.response?.status >= 400) {
            this.error.campaigns = true;
          }
        } finally {
          this.pending.marketingV2Campaigns = false;
        }
      }
    },
    async getMarketingV2Ads(
      { brandId, sourceCampaignIds, sourceAdIds, sort, search, effectiveStatuses, limit, offset },
      axiosConfig,
    ) {
      try {
        this.pending.marketingV2Ads = true;
        const response = await FacebookAllAPI.getMarketingV2Ads(
          {
            brandId,
            sourceCampaignIds,
            sourceAdIds,
            sort,
            search,
            effectiveStatuses,
            limit,
            offset: this.marketingV2AdsNextOffset || undefined,
          },
          axiosConfig,
        );
        const payload = response?.data;
        if (payload) {
          this.marketingV2Ads = payload?.data;
          this.marketingV2AdsCount = payload?.paging?.count || 0;

          if (payload?.paging?.next) {
            const url = new URL(payload.paging.next);
            const nextOffset = url.searchParams.get('offset');
            this.marketingV2AdsNextOffset = nextOffset ? parseInt(nextOffset, 10) : null;
          } else {
            this.marketingV2AdsNextOffset = null;
          }
        }
      } finally {
        this.pending.marketingV2Ads = false;
      }
    },
    async clearMarketingV2Ads() {
      this.marketingV2Ads = [];
      this.marketingV2AdsNextOffset = null;
      this.marketingV2AdsCount = 0;
    },
    async updateFbAccount({ brandId, fbAccountId, isMonitored }) {
      return FacebookAPI.updateAccount({ brandId, fbAccountId, isMonitored });
    },
    async deleteFbAccount({ brandId, fbAccountId }) {
      return FacebookAPI.deleteAccount({ brandId, fbAccountId });
    },
    async getPageFollowersCSV({ brandId, startDate, endDate, filename, scale }) {
      this.exportPending = true;
      try {
        const response = await FacebookAPI.getPageFollowersCSV({
          brandId,
          startDate,
          endDate,
          filename,
          scale,
        });
        downloadFileFromMemory(response?.data, response?.config?.params?.filename);
      } finally {
        this.exportPending = false;
      }
    },
    async sendImagesToMetaAds({ brandId, adAccountIds, imageUrls }) {
      this.pending.sendImagesToMetaAds = true;
      try {
        return FacebookAllAPI.sendImagesToMetaAds({ brandId, adAccountIds, imageUrls });
      } finally {
        this.pending.sendImagesToMetaAds = false;
      }
    },
    async createMarketingV2Ads({ brandId, sourceAdAccountId, ads }) {
      this.pending.createMarketingV2Ads = true;
      try {
        return FacebookAllAPI.createMarketingV2Ads({
          brandId,
          sourceAdAccountId,
          ads,
        });
      } finally {
        this.pending.createMarketingV2Ads = false;
      }
    },
  },
});
